import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Redirect, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { CloudDownloadRounded as DownloadIcon } from '@material-ui/icons';
import styled from 'styled-components';
import moment from 'moment';

import { LeaveAnalyticsReport, LeaveAnalyticsWhoIsOff } from 'domain-types';
import {
  AuthContext,
  baseUrl,
  isOrgFeatureEnabled,
  localizeLeaveType,
  useSpot
} from 'framework';
import { HorizontalLoadingBar, Table } from 'components';
import { StaffTabs } from './staff-tabs';

// ─── Styled components ───────────────────────────────────────────────────────

const PeriodBar = styled(Box)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${p => p.theme.spacing(2)}px;
  margin-bottom: ${p => p.theme.spacing(2)}px;
`;

const StatsGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: ${p => p.theme.spacing(2)}px;
  margin-bottom: ${p => p.theme.spacing(3)}px;
`;

const StatCard = styled(Box)`
  background: ${p => p.theme.palette.background.paper};
  border: 1px solid ${p => p.theme.palette.divider};
  border-radius: ${p => p.theme.shape.borderRadius}px;
  padding: ${p => p.theme.spacing(2)}px;
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing(0.5)}px;
`;

const StatValue = styled(Typography)`
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 1.2;
`;

const SectionTitle = styled(Typography)`
  margin-top: ${p => p.theme.spacing(3)}px;
  margin-bottom: ${p => p.theme.spacing(1)}px;
`;

const BreakdownRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing(1)}px;
  margin-bottom: ${p => p.theme.spacing(0.5)}px;
`;

const BreakdownBar = styled(Box)<{ $pct: number }>`
  flex: 1;
  height: 8px;
  border-radius: 4px;
  background: ${p => p.theme.palette.action.selected};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: ${p => p.$pct}%;
    background: ${p => p.theme.palette.primary.main};
    border-radius: 4px;
  }
`;

// ─── Preset helpers ──────────────────────────────────────────────────────────

type PeriodPreset =
  | 'thisMonth'
  | 'lastMonth'
  | 'thisQuarter'
  | 'lastQuarter'
  | 'thisYear'
  | 'custom';

const getPresetRange = (preset: PeriodPreset): { from: string; to: string } => {
  const today = moment();
  switch (preset) {
    case 'thisMonth':
      return {
        from: today
          .clone()
          .startOf('month')
          .format('YYYY-MM-DD'),
        to: today
          .clone()
          .endOf('month')
          .format('YYYY-MM-DD')
      };
    case 'lastMonth':
      return {
        from: today
          .clone()
          .subtract(1, 'month')
          .startOf('month')
          .format('YYYY-MM-DD'),
        to: today
          .clone()
          .subtract(1, 'month')
          .endOf('month')
          .format('YYYY-MM-DD')
      };
    case 'thisQuarter':
      return {
        from: today
          .clone()
          .startOf('quarter')
          .format('YYYY-MM-DD'),
        to: today
          .clone()
          .endOf('quarter')
          .format('YYYY-MM-DD')
      };
    case 'lastQuarter':
      return {
        from: today
          .clone()
          .subtract(1, 'quarter')
          .startOf('quarter')
          .format('YYYY-MM-DD'),
        to: today
          .clone()
          .subtract(1, 'quarter')
          .endOf('quarter')
          .format('YYYY-MM-DD')
      };
    case 'thisYear':
      return {
        from: today
          .clone()
          .startOf('year')
          .format('YYYY-MM-DD'),
        to: today
          .clone()
          .endOf('year')
          .format('YYYY-MM-DD')
      };
    default:
      return {
        from: today
          .clone()
          .startOf('month')
          .format('YYYY-MM-DD'),
        to: today
          .clone()
          .endOf('month')
          .format('YYYY-MM-DD')
      };
  }
};

// ─── CSV export ──────────────────────────────────────────────────────────────

const buildCsv = (
  whoIsOff: LeaveAnalyticsWhoIsOff[],
  t: (key: string) => string
): string => {
  const headers = [
    t('name'),
    t('leaveType'),
    t('startDate'),
    t('endDate'),
    t('leaveAnalyticsHours')
  ];
  const rows = whoIsOff.map(e => [
    `"${e.name.replace(/"/g, '""')}"`,
    `"${localizeLeaveType(t, e.type)}"`,
    e.startDate,
    e.endDate ?? '',
    String(e.hours)
  ]);
  return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
};

const downloadCsv = (csv: string, filename: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// ─── Main component ──────────────────────────────────────────────────────────

export function LeaveAnalytics() {
  const { organization } = useParams<{ organization: string }>();
  const { t } = useTranslation();
  const { getToken, isAdministrator } = useContext(AuthContext);
  const { data, query } = useSpot();
  const org = data?.organizations?.[organization];

  const [preset, setPreset] = useState<PeriodPreset>('thisMonth');
  const [customFrom, setCustomFrom] = useState<moment.Moment | null>(
    moment().startOf('month')
  );
  const [customTo, setCustomTo] = useState<moment.Moment | null>(
    moment().endOf('month')
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState<LeaveAnalyticsReport | null>(null);

  useEffect(() => {
    if (!org) {
      query(`org/${organization}`, {}, ['organizations', organization]);
    }
  }, [org, organization, query]);

  const resolvedRange = useCallback((): { from: string; to: string } | null => {
    if (preset !== 'custom') {
      return getPresetRange(preset);
    }
    if (!customFrom?.isValid() || !customTo?.isValid()) {
      return null;
    }
    if (customFrom.isAfter(customTo)) {
      return null;
    }
    return {
      from: customFrom.format('YYYY-MM-DD'),
      to: customTo.format('YYYY-MM-DD')
    };
  }, [preset, customFrom, customTo]);

  const fetchReport = useCallback(async () => {
    if (!org || !isOrgFeatureEnabled(org, 'leave')) {
      return;
    }
    const range = resolvedRange();
    if (!range) {
      return;
    }

    setLoading(true);
    setError('');
    setReport(null);

    try {
      const response = await fetch(
        `${baseUrl}/org/${organization}/reports/leave-analytics?from=${range.from}&to=${range.to}`,
        {
          headers: {
            Authorization: `Bearer ${getToken().getJwtToken()}`
          }
        }
      );

      if (!response.ok) {
        let serverError = t('leaveAnalyticsLoadFailed');
        try {
          const payload = (await response.json()) as { error?: string };
          if (payload.error) {
            serverError = payload.error;
          }
        } catch {
          // fallback to default translated message
        }
        throw new Error(serverError);
      }

      const payload = (await response.json()) as LeaveAnalyticsReport;
      setReport(payload);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('leaveAnalyticsLoadFailed')
      );
    } finally {
      setLoading(false);
    }
  }, [org, organization, getToken, t, resolvedRange]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  if (!isAdministrator()) {
    return <Redirect to={`/${organization}/users`} />;
  }

  const range = resolvedRange();

  const onExportCsv = () => {
    if (!report?.whoIsOff.length) return;
    const csv = buildCsv(report.whoIsOff, t);
    const filename = `leave-analytics-${range?.from ?? 'report'}-${range?.to ??
      ''}.csv`;
    downloadCsv(csv, filename);
  };

  const renderContent = () => {
    if (error) {
      return (
        <Box mt={2}>
          <Typography color="error">{error}</Typography>
        </Box>
      );
    }

    if (!report) {
      return null;
    }

    const { whoIsOff, stats } = report;
    const leaveTypeEntries = Object.entries(stats.byLeaveType).sort(
      ([, a], [, b]) => b.days - a.days
    );

    return (
      <>
        {/* ── Stats panel ── */}
        <SectionTitle variant="h6">{t('leaveAnalyticsStats')}</SectionTitle>
        <StatsGrid>
          <StatCard>
            <Typography variant="caption" color="textSecondary">
              {t('leaveAnalyticsTotalDays')}
            </Typography>
            <StatValue>{stats.totalLeaveDays}</StatValue>
          </StatCard>
          <StatCard>
            <Typography variant="caption" color="textSecondary">
              {t('leaveAnalyticsAvgDays')}
            </Typography>
            <StatValue>{stats.avgDaysPerEmployee}</StatValue>
          </StatCard>
          <StatCard>
            <Typography variant="caption" color="textSecondary">
              {t('leaveAnalyticsAbsenceRate')}
            </Typography>
            <StatValue>{`${stats.absenceRate}%`}</StatValue>
          </StatCard>
          <StatCard>
            <Typography variant="caption" color="textSecondary">
              {t('leaveAnalyticsSickCount')}
            </Typography>
            <StatValue>{stats.sickLeaveCount}</StatValue>
          </StatCard>
        </StatsGrid>

        {/* ── Breakdown by leave type ── */}
        {leaveTypeEntries.length > 0 && (
          <>
            <SectionTitle variant="subtitle1">
              {t('leaveAnalyticsBreakdown')}
            </SectionTitle>
            {leaveTypeEntries.map(([type, breakdown]) => (
              <BreakdownRow key={type}>
                <Box minWidth={130}>
                  <Typography variant="body2">
                    {localizeLeaveType(t, type)}
                  </Typography>
                </Box>
                <BreakdownBar $pct={breakdown.percentage} />
                <Box minWidth={80} textAlign="right">
                  <Typography variant="body2">
                    {`${breakdown.days} ${t('days')} (${
                      breakdown.percentage
                    }%)`}
                  </Typography>
                </Box>
              </BreakdownRow>
            ))}
          </>
        )}

        {/* ── Peak absence weeks ── */}
        {stats.peakAbsenceWeeks.length > 0 && (
          <>
            <SectionTitle variant="subtitle1">
              {t('leaveAnalyticsPeakWeeks')}
            </SectionTitle>
            <Table
              headers={[
                t('leaveAnalyticsWeekStart'),
                t('leaveAnalyticsWeekEnd'),
                t('leaveAnalyticsHeadcount')
              ]}
              rows={stats.peakAbsenceWeeks.map(week => ({
                id: week.weekStart,
                cells: [week.weekStart, week.weekEnd, String(week.headcount)]
              }))}
            />
          </>
        )}

        {/* ── Who's off ── */}
        <SectionTitle variant="h6">
          {t('leaveAnalyticsWhoIsOff')}
          {whoIsOff.length > 0 && (
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={onExportCsv}
              style={{ marginLeft: 16 }}
            >
              {t('leaveAnalyticsExportCsv')}
            </Button>
          )}
        </SectionTitle>
        {whoIsOff.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            {t('leaveAnalyticsEmpty')}
          </Typography>
        ) : (
          <Table
            headers={[
              t('name'),
              t('leaveType'),
              t('startDate'),
              t('endDate'),
              t('leaveAnalyticsHours')
            ]}
            rows={whoIsOff.map(entry => ({
              id: `${entry.userId}-${entry.startDate}-${entry.type}`,
              cells: [
                entry.name,
                localizeLeaveType(t, entry.type),
                entry.startDate,
                entry.endDate ?? '—',
                String(entry.hours)
              ]
            }))}
          />
        )}
      </>
    );
  };

  return (
    <StaffTabs organization={organization} selected="leaveAnalytics">
      {/* ── Period selector ── */}
      <PeriodBar mt={2}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
          <InputLabel>{t('leaveAnalyticsPeriod')}</InputLabel>
          <Select
            value={preset}
            label={t('leaveAnalyticsPeriod')}
            onChange={e => setPreset(e.target.value as PeriodPreset)}
          >
            <MenuItem value="thisMonth">
              {t('leaveAnalyticsThisMonth')}
            </MenuItem>
            <MenuItem value="lastMonth">
              {t('leaveAnalyticsLastMonth')}
            </MenuItem>
            <MenuItem value="thisQuarter">
              {t('leaveAnalyticsThisQuarter')}
            </MenuItem>
            <MenuItem value="lastQuarter">
              {t('leaveAnalyticsLastQuarter')}
            </MenuItem>
            <MenuItem value="thisYear">{t('leaveAnalyticsThisYear')}</MenuItem>
            <MenuItem value="custom">{t('leaveAnalyticsCustom')}</MenuItem>
          </Select>
        </FormControl>

        {preset === 'custom' && (
          <>
            <KeyboardDatePicker
              disableToolbar
              variant="inline"
              format="YYYY-MM-DD"
              size="small"
              inputVariant="outlined"
              label={t('leaveAnalyticsFrom')}
              value={customFrom}
              onChange={setCustomFrom}
            />
            <KeyboardDatePicker
              disableToolbar
              variant="inline"
              format="YYYY-MM-DD"
              size="small"
              inputVariant="outlined"
              label={t('leaveAnalyticsTo')}
              value={customTo}
              onChange={setCustomTo}
            />
          </>
        )}

        {!loading && range && (
          <Typography variant="body2" color="textSecondary">
            {`${range.from} - ${range.to}`}
          </Typography>
        )}
      </PeriodBar>

      <HorizontalLoadingBar loading={loading} />
      {renderContent()}
    </StaffTabs>
  );
}
